/**
 * Live interview PostHog contract - product analytics only (`live_interview.*`).
 * `flow_id` is always the room id (uppercase) once the room is known; `interview_data_id`
 * is a separate dimension for Supabase joins.
 */

/** Step values for `live_interview.*` events (PostHog payload). */
export type LiveInterviewStep = 'join' | 'preflight' | 'in_call' | 'ending' | 'done';

/**
 * `live_interview.abandoned` reason - signal-derived, not a guess at browser “intent”.
 */
export type LiveInterviewAbandonedReason =
  | 'hidden_bfcache'
  | 'unload'
  | 'reloaded'
  | 'in_call_offline'
  | 'connection_lost'
  | 'unknown';

/**
 * `live_interview.disconnected` when the server ends without `interview_properly_ended`.
 * `incomplete` = user tab close, grace expiry, or other non-categorized end.
 */
export type LiveInterviewDisconnectReason =
  | 'incomplete'
  | 'unrecoverable_connection'
  | 'unrecoverable_backend';

export type LiveInterviewCompletionInitiator = 'user' | 'ai' | 'system';

export type LiveInterviewBlockReason =
  | 'invalid_auth'
  | 'room_occupied'
  | 'insufficient_credits'
  | 'schedule_closed'
  | 'policy_blocked';

const LIVE_INTERVIEW_EVENT_NAMES_CONST = [
  'live_interview.join_viewed',
  'live_interview.join_submitted',
  'live_interview.join_validated',
  'live_interview.join_rejected',
  'live_interview.preflight_viewed',
  'live_interview.start_clicked',
  'live_interview.session_connected',
  'live_interview.first_user_turn',
  'live_interview.control_mic_toggled',
  'live_interview.control_camera_toggled',
  'live_interview.control_captions_toggled',
  'live_interview.control_info_panel_toggled',
  'live_interview.control_screen_share_clicked',
  'live_interview.control_nudge_clicked',
  'live_interview.control_more_menu_toggled',
  'live_interview.control_report_issue_opened',
  'live_interview.control_report_issue_submitted',
  'live_interview.control_end_clicked',
  'live_interview.control_end_confirmed',
  'live_interview.control_end_cancelled',
  'live_interview.screen_share_prompt_shown',
  'live_interview.screen_share_started',
  'live_interview.screen_share_stopped',
  'live_interview.screen_share_declined',
  'live_interview.abandoned',
  'live_interview.completed',
  'live_interview.blocked',
  'live_interview.timed_out',
  'live_interview.disconnected',
] as const;

export type LiveInterviewEventName = (typeof LIVE_INTERVIEW_EVENT_NAMES_CONST)[number];

export const LIVE_INTERVIEW_EVENT_NAMES: readonly LiveInterviewEventName[] = LIVE_INTERVIEW_EVENT_NAMES_CONST;

/**
 * Build base properties: `flow_id` and `room_id` are both the canonical room id.
 */
export function buildLiveInterviewBaseProps(
  roomId: string,
  step: LiveInterviewStep,
  interviewDataId?: string | null,
): { flow_id: string; room_id: string; step: LiveInterviewStep; interview_data_id?: string } {
  const id = roomId.trim().toUpperCase();
  const out: { flow_id: string; room_id: string; step: LiveInterviewStep; interview_data_id?: string } = {
    flow_id: id,
    room_id: id,
    step,
  };
  if (interviewDataId && interviewDataId.trim().length > 0) {
    out.interview_data_id = interviewDataId.trim();
  }
  return out;
}

/**
 * Classify `live_interview.abandoned` from page lifecycle and light client state.
 */
export function classifyLiveInterviewAbandoned(
  e: PageTransitionEvent,
  ctx: { wasOffline: boolean; clientSocketClosed: boolean },
): LiveInterviewAbandonedReason {
  if (e.persisted) {
    return 'hidden_bfcache';
  }
  if (ctx.wasOffline) {
    return 'in_call_offline';
  }
  if (ctx.clientSocketClosed) {
    return 'connection_lost';
  }
  return 'unload';
}
